import { useCallback, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { FaArrowRight, FaNewspaper, FaRegCalendarAlt, FaRegClock, FaSearch, FaUser } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { fetchBlogCategories, fetchBlogs } from '../services/blogService';
import '../styles/blog.css';

const PER_PAGE = 9;

const BlogList = () => {
    const [blogs, setBlogs] = useState([]);
    const [categories, setCategories] = useState([]);
    const [meta, setMeta] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [search, setSearch] = useState('');
    const [activeCategory, setActiveCategory] = useState('');
    const pageRef = useRef(1);
    const sentinelRef = useRef(null);
    const searchTimer = useRef(null);

    const loadCategories = useCallback(async () => {
        try {
            const res = await fetchBlogCategories();
            if (res.success) setCategories(res.data);
        } catch {
            /* silent */
        }
    }, []);

    const loadBlogs = useCallback(
        async (pageNum, append = false) => {
            if (pageNum === 1) setLoading(true);
            else setLoadingMore(true);

            try {
                const params = { page: pageNum, per_page: PER_PAGE };
                if (search) params.q = search;
                if (activeCategory) params.category_id = activeCategory;

                const res = await fetchBlogs(params);
                if (res.success) {
                    setBlogs((prev) => (append ? [...prev, ...res.data] : res.data));
                    setMeta(res.meta);
                    setHasMore(res.meta.has_more);
                } else {
                    if (!append) setBlogs([]);
                    setHasMore(false);
                }
            } catch {
                if (!append) setBlogs([]);
                setHasMore(false);
            } finally {
                setLoading(false);
                setLoadingMore(false);
            }
        },
        [search, activeCategory]
    );

    // Initial load
    useEffect(() => {
        loadCategories();
    }, [loadCategories]);

    useEffect(() => {
        pageRef.current = 1;
        setHasMore(true);
        loadBlogs(1, false);
    }, [loadBlogs]);

    // Search debounce
    const handleSearchChange = (e) => {
        const val = e.target.value;
        setSearch(val);
        clearTimeout(searchTimer.current);
        searchTimer.current = setTimeout(() => {
            pageRef.current = 1;
            setHasMore(true);
            loadBlogs(1, false);
        }, 400);
    };

    const handleCategoryClick = (catId) => {
        setActiveCategory((prev) => (prev === catId ? '' : catId));
        pageRef.current = 1;
        setHasMore(true);
        // will trigger useEffect above
    };

    // Infinite scroll observer
    useEffect(() => {
        const sentinel = sentinelRef.current;
        if (!sentinel || !hasMore || loading) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && hasMore && !loadingMore) {
                    pageRef.current += 1;
                    loadBlogs(pageRef.current, true);
                }
            },
            { rootMargin: '200px' }
        );

        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [hasMore, loadingMore, loading, loadBlogs]);

    const formatDate = (iso) => {
        if (!iso) return '';
        return new Date(iso).toLocaleDateString('id-ID', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
        });
    };

    return (
        <div className="blog-page">
            <Helmet>
                <title>Blog & Artikel Properti Terbaru — Urbania</title>
                <meta
                    name="description"
                    content="Temukan artikel, tips, dan berita properti terbaru dari Urbania untuk membantu Anda menemukan properti impian."
                />
            </Helmet>

            <div className="blog-container">
                {/* HERO */}
                <div className="blog-hero">
                    <h1>Blog &amp; Artikel</h1>
                    <p>Tips properti, berita industri, dan panduan lengkap.</p>
                </div>

                {/* SEARCH */}
                <div className="blog-search-wrap">
                    <FaSearch className="search-icon" size={15} />
                    <input type="text" placeholder="Cari artikel..." value={search} onChange={handleSearchChange} />
                </div>

                {/* CATEGORY CHIPS */}
                <div className="blog-categories">
                    <span
                        className={`cat-chip ${activeCategory === '' ? 'active' : ''}`}
                        onClick={() => handleCategoryClick('')}
                    >
                        Semua
                    </span>
                    {categories.map((cat) => (
                        <span
                            key={cat.id}
                            className={`cat-chip ${activeCategory === String(cat.id) ? 'active' : ''}`}
                            onClick={() => handleCategoryClick(String(cat.id))}
                        >
                            {cat.name}
                            <span className="count">{cat.blogs_count}</span>
                        </span>
                    ))}
                </div>

                {/* CONTENT */}
                {loading ? (
                    <div className="blog-grid blog-grid-skeleton">
                        {Array.from({ length: 6 }).map((_, i) => (
                            <div className="blog-card-skeleton" key={i}>
                                <div className="skeleton skeleton-card-image" />
                                <div className="skeleton-card-body">
                                    <div className="skeleton skeleton-card-meta-line" />
                                    <div className="skeleton skeleton-card-title-line" />
                                    <div className="skeleton skeleton-card-title-line-short" />
                                    <div className="skeleton skeleton-card-excerpt-line" />
                                    <div className="skeleton skeleton-card-excerpt-line" />
                                    <div className="skeleton-card-footer-sk">
                                        <div className="skeleton skeleton-card-tag-sk" />
                                        <div className="skeleton skeleton-card-read-sk" />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : blogs.length === 0 ? (
                    <div className="blog-empty">
                        <FaNewspaper size={48} color="#e5e7eb" />
                        <h3>Belum Ada Artikel</h3>
                        <p>Belum ada artikel yang dipublikasikan. Silakan kembali lagi nanti.</p>
                    </div>
                ) : (
                    <>
                        <div className="blog-grid">
                            {blogs.map((blog) => (
                                <article className="blog-card" key={blog.id}>
                                    <Link to={`/blog/${blog.slug}`} className="blog-card-image">
                                        {blog.featured_image ? (
                                            <img src={blog.featured_image} alt={blog.title} loading="lazy" />
                                        ) : (
                                            <div className="placeholder">
                                                <FaNewspaper size={36} />
                                            </div>
                                        )}
                                        {blog.category && (
                                            <span className="blog-card-category">{blog.category.name}</span>
                                        )}
                                    </Link>
                                    <div className="blog-card-body">
                                        <div className="blog-card-meta">
                                            {blog.author && (
                                                <span>
                                                    <FaUser size={11} /> {blog.author.name}
                                                </span>
                                            )}
                                            <span>
                                                <FaRegClock size={11} /> {blog.reading_time} menit
                                            </span>
                                            <span>
                                                <FaRegCalendarAlt size={11} /> {formatDate(blog.published_at)}
                                            </span>
                                        </div>
                                        <h2 className="blog-card-title">
                                            <Link to={`/blog/${blog.slug}`}>{blog.title}</Link>
                                        </h2>
                                        {blog.excerpt && <p className="blog-card-excerpt">{blog.excerpt}</p>}
                                        <div className="blog-card-footer">
                                            {blog.tags && blog.tags.length > 0 && (
                                                <div className="blog-card-tags">
                                                    {blog.tags.slice(0, 3).map((tag) => (
                                                        <span className="tag" key={tag.id}>
                                                            {tag.name}
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                            <Link to={`/blog/${blog.slug}`} className="blog-card-readmore">
                                                Baca <FaArrowRight size={11} />
                                            </Link>
                                        </div>
                                    </div>
                                </article>
                            ))}
                        </div>

                        {/* Infinite scroll sentinel */}
                        {hasMore && <div ref={sentinelRef} className="blog-sentinel" />}

                        {loadingMore && (
                            <div className="blog-load-more">
                                <div className="loader" />
                            </div>
                        )}

                        {!hasMore && blogs.length > 0 && (
                            <div className="blog-end">
                                <hr />
                                <p>Semua artikel telah ditampilkan</p>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default BlogList;
